/**
 * ClawInstall light/dark mode atom.
 *
 * The mode is mirrored to `localStorage` (so it survives refreshes) and applied
 * as a `data-mode` attribute on the `.theme-clawinstall` layout root, which the
 * dark token block in `globals.css` keys off. Initial value: the persisted
 * choice if any, else the OS preference (`prefers-color-scheme`), else light —
 * i.e. an explicit toggle overrides the OS default.
 *
 * SSR/jsdom-safe: every `window` / `localStorage` / `matchMedia` access is
 * guarded so module load never throws where those APIs are absent.
 *
 * @since 1.0.0
 */
import { Atom } from "@effect-atom/atom-react";

export type ClawInstallMode = "light" | "dark";

export const CLAWINSTALL_THEME_KEY = "clawinstall-theme";

/** Minimal shape of the browser globals this module reads. */
export interface ThemeWindowLike {
  readonly localStorage?: {
    readonly getItem: (key: string) => string | null;
    readonly setItem: (key: string, value: string) => void;
  };
  readonly matchMedia?: (query: string) => { readonly matches: boolean } | undefined;
}

/**
 * Pure mode resolver from a window-like env (`null` = SSR). Persisted choice
 * wins, then OS preference, then light. Exported for testing. Optional chaining
 * tolerates an absent `localStorage` / `matchMedia` (e.g. jsdom, SSR).
 *
 * @since 1.0.0
 */
export const computeInitialMode = (win: ThemeWindowLike | null): ClawInstallMode => {
  if (win === null) return "light";
  const stored = win.localStorage?.getItem(CLAWINSTALL_THEME_KEY);
  if (stored === "light" || stored === "dark") return stored;
  return win.matchMedia?.("(prefers-color-scheme: dark)")?.matches === true ? "dark" : "light";
};

/**
 * Resolve the initial mode from the real `window` (or SSR).
 *
 * @since 1.0.0
 */
export const getInitialClawInstallMode = (): ClawInstallMode => {
  if (typeof window === "undefined") return computeInitialMode(null);
  return computeInitialMode(window as ThemeWindowLike);
};

/**
 * Persist the active mode to `localStorage`. No-op during SSR / when storage is
 * unavailable.
 *
 * @since 1.0.0
 */
export const persistClawInstallMode = (mode: ClawInstallMode): void => {
  if (typeof window === "undefined") return;
  (window as ThemeWindowLike).localStorage?.setItem(CLAWINSTALL_THEME_KEY, mode);
};

/** The opposite mode — the target of a toggle. @since 1.0.0 */
export const nextClawInstallMode = (mode: ClawInstallMode): ClawInstallMode => mode === "dark" ? "light" : "dark";

/**
 * Writable atom holding the active ClawInstall mode, seeded from
 * localStorage / OS preference at module init.
 *
 * @since 1.0.0
 */
export const clawInstallModeAtom = Atom.make<ClawInstallMode>(getInitialClawInstallMode());
